import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, LogOut, FileText, Download } from "lucide-react";
import { authHeader, clearToken, isAuthed } from "@/lib/auth";
import BlogAdmin from "@/components/BlogAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [contacts, setContacts] = useState([]);
  const [careers, setCareers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => { clearToken(); nav("/admin/login"); }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, k, n] = await Promise.all([
        axios.get(`${API}/contact`, { headers: authHeader() }),
        axios.get(`${API}/careers`, { headers: authHeader() }),
        axios.get(`${API}/newsletter`, { headers: authHeader() }),
      ]);
      setContacts(c.data);
      setCareers(k.data);
      setSubs(n.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!isAuthed()) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const downloadResume = async (id, filename) => {
    try {
      const res = await axios.get(`${API}/careers/${id}/resume`, { headers: authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert("Could not open the resume.");
    }
  };

  return (
    <div className="min-h-screen bg-konattu-cream" data-testid="admin-page">
      <div className="border-b border-konattu-charcoal/10 bg-konattu-cream/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-konattu-red" data-testid="admin-back">
            <ArrowLeft size={14} /> KONATTU · ADMIN
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-konattu-charcoal/70 hover:text-konattu-red transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="font-display font-bold text-4xl text-konattu-charcoal tracking-tight">Dashboard</h1>
          <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 border border-konattu-charcoal/20 px-5 py-2.5 text-sm hover:border-konattu-red hover:text-konattu-red transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="flex gap-1 mb-8 border-b border-konattu-charcoal/15">
          {[["enquiries", `Enquiries (${contacts.length})`], ["applications", `Applications (${careers.length})`], ["subscribers", `Subscribers (${subs.length})`], ["blog", "Blog"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-5 py-3 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === k ? "border-konattu-red text-konattu-red" : "border-transparent text-konattu-charcoal/50 hover:text-konattu-charcoal"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "blog" ? (
          <BlogAdmin />
        ) : loading ? (
          <p className="font-body text-konattu-charcoal/60">Loading...</p>
        ) : tab === "enquiries" ? (
          contacts.length === 0 ? (
            <div className="border border-konattu-charcoal/15 p-16 text-center" data-testid="admin-empty"><p className="text-konattu-charcoal/60">No enquiries yet.</p></div>
          ) : (
            <div className="space-y-3" data-testid="admin-enquiry-list">
              {contacts.map((c) => (
                <div key={c.id} className="bg-white border border-konattu-charcoal/10 p-6" data-testid="admin-contact-row">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-3">
                      <span className="font-display font-semibold text-lg text-konattu-charcoal">{c.name}</span>
                      <span className="bg-konattu-red/10 text-konattu-red px-2.5 py-1 text-[11px] font-mono tracking-wide">{c.interest}</span>
                    </div>
                    <span className="font-mono text-[11px] text-konattu-charcoal/40">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <div className="font-body text-sm text-konattu-charcoal/70 mb-2">{c.email} · {c.phone}</div>
                  <p className="font-body text-konattu-charcoal/85">{c.message}</p>
                </div>
              ))}
            </div>
          )
        ) : tab === "applications" ? (
          careers.length === 0 ? (
          <div className="border border-konattu-charcoal/15 p-16 text-center" data-testid="admin-empty-careers"><p className="text-konattu-charcoal/60">No applications yet.</p></div>
          ) : (
          <div className="space-y-3" data-testid="admin-application-list">
            {careers.map((c) => (
              <div key={c.id} className="bg-white border border-konattu-charcoal/10 p-6" data-testid="admin-career-row">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-display font-semibold text-lg text-konattu-charcoal">{c.name}</span>
                    <span className="bg-konattu-charcoal/10 text-konattu-charcoal px-2.5 py-1 text-[11px] font-mono tracking-wide">{c.position}</span>
                  </div>
                  <span className="font-mono text-[11px] text-konattu-charcoal/40">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="font-body text-sm text-konattu-charcoal/70 mb-2">{c.email} · {c.phone}</div>
                {c.message && <p className="font-body text-konattu-charcoal/85 mb-3">{c.message}</p>}
                {c.has_resume ? (
                  <button onClick={() => downloadResume(c.id, c.resume_filename)} data-testid="admin-resume-download" className="inline-flex items-center gap-2 text-sm text-konattu-red hover:underline">
                    <FileText size={15} /> {c.resume_filename} <Download size={14} />
                  </button>
                ) : (
                  <span className="text-xs font-mono text-konattu-charcoal/40">NO RESUME ATTACHED</span>
                )}
              </div>
            ))}
          </div>
          )
        ) : subs.length === 0 ? (
          <div className="border border-konattu-charcoal/15 p-16 text-center" data-testid="admin-empty-subs"><p className="text-konattu-charcoal/60">No subscribers yet.</p></div>
        ) : (
          <div className="space-y-2" data-testid="admin-subscriber-list">
            {subs.map((s) => (
              <div key={s.id} className="bg-white border border-konattu-charcoal/10 px-6 py-4 flex items-center justify-between" data-testid="admin-sub-row">
                <span className="font-body text-konattu-charcoal">{s.email}</span>
                <span className="font-mono text-[11px] text-konattu-charcoal/40">{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
